import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OwnerEntity, UserRole } from '@bhops/shared';
import { storesApi, type CreateStoreBody } from '@/api/stores';
import { customersApi } from '@/api/customers';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Plan v0.3.0 §F-3 follow-up — when launched from
   * CustomerMasterPage's embedded StoresPage, pre-select this
   * customer so operators don't have to re-pick the row they
   * were already viewing. Falls through to the normal customer
   * picker dropdown when undefined.
   */
  initialCustomerId?: string;
}

export function StoreCreateModal({ open, onClose, initialCustomerId }: Props) {
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<CreateStoreBody>({
    name: '',
    ownerEntity: user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB,
    country: 'KR',
    ...(initialCustomerId ? { customerId: initialCustomerId } : {}),
  });
  const [error, setError] = useState<string | null>(null);

  // 추가수정 2차(26-07-27) §1 — 고객사 드롭다운은 서버 상한(200곳)까지만 받아
  // 온다. 고객사가 그보다 많아지면 뒤쪽이 조용히 잘려 "우리 고객사가 목록에
  // 없다"가 된다. 근본 수정(검색/페이징)은 후속(부록 A-6)이고, 이번엔 잘렸다는
  // 사실만 알린다 — 문구는 고객사 서류 화면의 기존 잘림 안내와 같은 패턴.
  const customersQ = useQuery({
    queryKey: ['customers', { pageSize: 200 }],
    queryFn: () => customersApi.list({ pageSize: 200 }),
    enabled: open,
  });
  const customerRows = customersQ.data?.items ?? [];
  const customersTruncated = (customersQ.data?.total ?? 0) > customerRows.length;

  const create = useMutation({
    mutationFn: (body: CreateStoreBody) => storesApi.create(body),
    onSuccess: (store) => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      toast.success(
        `${store.name}${store.branchName ? ' ' + store.branchName : ''} 매장이 등록되었습니다.`,
        '등록 완료',
      );
      onClose();
      setForm({
        name: '',
        ownerEntity: user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB,
        country: 'KR',
      });
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    create.mutate(form);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 매장 등록"
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="store-create-form" disabled={create.isPending}>
            {create.isPending ? '저장 중…' : '등록'}
          </Button>
        </>
      }
    >
      <form id="store-create-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="매장명" htmlFor="s-name" required>
            <Input
              id="s-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="지점명" htmlFor="s-branch">
            <Input
              id="s-branch"
              value={form.branchName ?? ''}
              onChange={(e) => setForm({ ...form, branchName: e.target.value || undefined })}
              placeholder="예: 홍대점"
            />
          </Field>
        </div>

        <Field label="고객사" htmlFor="s-customer">
          <Select
            id="s-customer"
            value={form.customerId ?? ''}
            onChange={(e) => setForm({ ...form, customerId: e.target.value || undefined })}
          >
            <option value="">연결된 고객사 없음</option>
            {customerRows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </Select>
          {customersTruncated && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--fg-muted)' }}>
              전체 {(customersQ.data?.total ?? 0).toLocaleString()}곳 중 {customerRows.length}곳만
              표시됩니다.
            </div>
          )}
        </Field>

        <Field label="주소" htmlFor="s-addr">
          <Input
            id="s-addr"
            value={form.addressLine1 ?? ''}
            onChange={(e) => setForm({ ...form, addressLine1: e.target.value || undefined })}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="도시" htmlFor="s-city">
            <Input
              id="s-city"
              value={form.city ?? ''}
              onChange={(e) => setForm({ ...form, city: e.target.value || undefined })}
            />
          </Field>
          <Field label="지역" htmlFor="s-region">
            <Input
              id="s-region"
              value={form.region ?? ''}
              onChange={(e) => setForm({ ...form, region: e.target.value || undefined })}
            />
          </Field>
          <Field label="국가" htmlFor="s-country">
            <Input
              id="s-country"
              value={form.country ?? 'KR'}
              maxLength={2}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
        </div>

        <Field label="소유 법인" htmlFor="s-owner" required>
          <Select
            id="s-owner"
            value={form.ownerEntity}
            disabled={user?.role !== UserRole.ADMIN}
            onChange={(e) => setForm({ ...form, ownerEntity: e.target.value as OwnerEntity })}
          >
            <option value={OwnerEntity.BEYOND_HONEYCOMB}>비욘드허니컴</option>
            <option value={OwnerEntity.HONEYCOMB_ROBOTICS}>허니컴로보틱스</option>
          </Select>
        </Field>

        <Field label="메모" htmlFor="s-notes">
          <Textarea
            id="s-notes"
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || undefined })}
            rows={3}
          />
        </Field>
      </form>
    </Modal>
  );
}
